import { GoogleAuthProvider, EmailAuthProvider, linkWithCredential, signInWithCredential, signInWithPopup, signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword, reauthenticateWithCredential, updatePassword as firebaseUpdatePassword, signOut as firebaseSignOut, onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebase';
import { createUserDocument } from './userService';
import { disablePush } from './pushService';

async function createUserFromFirebaseUser(user: User): Promise<User> {
  const nameParts = (user.displayName ?? '').split(' ');
  const firstname = nameParts[0] ?? '';
  const lastname = nameParts.slice(1).join(' ');
  await createUserDocument(user.uid, user.email ?? '', firstname, lastname);
  return user;
}

export async function signInWithGoogle(idToken: string): Promise<User> {
  const credential = GoogleAuthProvider.credential(idToken);
  const userCredential = await signInWithCredential(auth, credential);
  return createUserFromFirebaseUser(userCredential.user);
}

export async function signInWithGooglePopup(): Promise<User> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: 'bieszczady.gopr.pl' });
  const userCredential = await signInWithPopup(auth, provider);
  const user = userCredential.user;
  if (!user.email?.endsWith('@bieszczady.gopr.pl')) {
    await firebaseSignOut(auth);
    throw new Error('Only @bieszczady.gopr.pl users are allowed');
  }
  return createUserFromFirebaseUser(user);
}

export async function signInWithEmailPassword(email: string, password: string): Promise<User> {
  const userCredential = await firebaseSignInWithEmailAndPassword(auth, email, password);
  return createUserFromFirebaseUser(userCredential.user);
}

export async function linkEmailPassword(password: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  if (!user.email) {
    throw new Error('User email not available');
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await linkWithCredential(user, credential);
}

export async function updateUserPassword(currentPassword: string, newPassword: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  if (!user.email) {
    throw new Error('User email not available');
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await firebaseUpdatePassword(user, newPassword);
}

export async function signOut(): Promise<void> {
  // Remove the account association before Firebase invalidates this session.
  await disablePush();
  await firebaseSignOut(auth);
}

export function subscribeToAuthState(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}
